import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useGetEvalTimelineQuery } from "../../../app/api/console/evalResultsApiSlice";
import {
  packVersionColor,
  formatMetric,
  formatDate,
  shortSha,
} from "./evalConfig";

// Line styling: TCQ is the headline series (solid, dots colored by
// pack_version); precision/recall are context (dashed/dotted grays) so the
// chart stays readable in grayscale.
const SERIES = [
  { key: "median_tcq", name: "Median TCQ", stroke: "#111827", dash: null, width: 2 },
  { key: "median_precision", name: "Median precision", stroke: "#6B7280", dash: "5 3", width: 1.5 },
  { key: "median_recall", name: "Median recall", stroke: "#9CA3AF", dash: "2 3", width: 1.5 },
];

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div className='bg-white rounded-xl border border-gray-200 shadow-lg px-4 py-3 text-xs max-w-[260px]'>
      <div className='font-semibold text-gray-900'>{formatDate(p.started_at)}</div>
      <div className='text-gray-400 mb-1.5 break-all'>{p.run_id}</div>
      {SERIES.map((s) => (
        <div key={s.key} className='flex items-center justify-between gap-4'>
          <span className='text-gray-500'>{s.name}</span>
          <span className='font-semibold text-gray-800'>{formatMetric(p[s.key])}</span>
        </div>
      ))}
      <div className='border-t border-gray-100 mt-1.5 pt-1.5 flex items-center justify-between gap-4'>
        <span className='text-gray-500'>Pack</span>
        <span className='font-semibold text-gray-800'>{p.pack_version || "—"}</span>
      </div>
      <div className='flex items-center justify-between gap-4'>
        <span className='text-gray-500'>Skill</span>
        <span className='font-mono text-gray-800'>{shortSha(p.skill_sha)}</span>
      </div>
      {p.run_kind && (
        <div className='flex items-center justify-between gap-4'>
          <span className='text-gray-500'>Kind</span>
          <span className='text-gray-800'>{p.run_kind}</span>
        </div>
      )}
    </div>
  );
};

function HistoryChart({ websiteId, agentType, runKind }) {
  const { data, isFetching, error } = useGetEvalTimelineQuery(
    { websiteId, agentType, runKind },
    { skip: !websiteId || !agentType }
  );

  const { points, packVersions } = useMemo(() => {
    const pts = (data?.points || []).map((p) => ({
      ...p,
      ts: new Date(p.started_at).getTime(),
    }));
    const versions = [];
    for (const p of pts) {
      if (p.pack_version && !versions.includes(p.pack_version)) {
        versions.push(p.pack_version);
      }
    }
    return { points: pts, packVersions: versions };
  }, [data]);

  // Dots on the TCQ line are colored by pack_version — a version bump is
  // visible as a color change along the series.
  const renderTcqDot = (props) => {
    const { cx, cy, payload, index } = props;
    if (cx === null || cy === null || cx === undefined || cy === undefined) return null;
    return (
      <circle
        key={`tcq-dot-${index}`}
        cx={cx}
        cy={cy}
        r={4}
        fill={packVersionColor(payload.pack_version, packVersions)}
        stroke='#fff'
        strokeWidth={1}
      />
    );
  };

  if (!websiteId || !agentType) {
    return (
      <div className='px-4 py-8 text-center text-sm text-gray-400'>
        Select a website + agent type to plot the metric history.
      </div>
    );
  }
  if (error) {
    return (
      <div className='px-4 py-6 text-sm text-red-600'>
        Failed to load timeline: {error?.data?.detail || error?.error || "unknown error"}
      </div>
    );
  }
  if (!isFetching && points.length === 0) {
    return (
      <div className='px-4 py-8 text-center text-sm text-gray-400'>
        No pushed runs yet for this website + agent.
      </div>
    );
  }

  return (
    <div>
      <div className='h-64'>
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
            <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' vertical={false} />
            <XAxis
              dataKey='ts'
              type='number'
              domain={["dataMin", "dataMax"]}
              scale='time'
              tickFormatter={(ts) =>
                new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })
              }
              tick={{ fontSize: 11, fill: "#6B7280" }}
              stroke='#D1D5DB'
            />
            <YAxis
              domain={[0, 1]}
              tickFormatter={(v) => v.toFixed(1)}
              tick={{ fontSize: 11, fill: "#6B7280" }}
              stroke='#D1D5DB'
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
            {SERIES.map((s) => (
              <Line
                key={s.key}
                type='monotone'
                dataKey={s.key}
                name={s.name}
                stroke={s.stroke}
                strokeWidth={s.width}
                strokeDasharray={s.dash || undefined}
                dot={s.key === "median_tcq" ? renderTcqDot : false}
                activeDot={{ r: 5 }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {packVersions.length > 0 && (
        <div className='flex flex-wrap items-center gap-3 px-1 pt-2'>
          <span className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold'>
            TCQ dots by pack
          </span>
          {packVersions.map((v) => (
            <span key={v} className='inline-flex items-center gap-1.5 text-xs text-gray-600'>
              <span
                className='w-2.5 h-2.5 rounded-full border border-white shadow-sm'
                style={{ backgroundColor: packVersionColor(v, packVersions) }}
              />
              {v}
            </span>
          ))}
          {data?.index_used === false && (
            <span className='text-[11px] text-gray-400'>· scan fallback (GSI not provisioned)</span>
          )}
        </div>
      )}
    </div>
  );
}

export default HistoryChart;
